import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

// ── Base Tabs (shadcn default) ────────────────────────────────────────────────

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

// ── TabsPill ──────────────────────────────────────────────────────────────────
// Usage: wrap Tabs + TabsPillList + TabsPillTrigger + TabsContent

const TabsPillList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      // Capsule rail: individually shadowed pills on the page background,
      // scrolling rather than wrapping. Replaces the old segmented-control look
      // (grey track, white active thumb), which had no consumers and did not
      // match the capsule language used elsewhere.
      "flex gap-2.5 overflow-x-auto pb-2 pt-1",
      "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      className
    )}
    {...props}
  />
))
TabsPillList.displayName = "TabsPillList"

const TabsPillTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Matches <Capsule variant="tab"> exactly — same radius, padding, weight
      // and elevation — so a Radix-driven tab rail and a hand-driven filter rail
      // are visually indistinguishable.
      "inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2",
      "min-h-[40px] text-sm font-bold leading-none tracking-[-0.005em] whitespace-nowrap",
      "transition-[transform,box-shadow,background-color,border-color] duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      "border-border bg-card text-foreground shadow-e1 hover:-translate-y-px hover:shadow-e2",
      // Active drops the shadow so it reads as pressed, not merely coloured.
      "data-[state=active]:border-success/25 data-[state=active]:bg-secondary",
      "data-[state=active]:text-success data-[state=active]:shadow-none data-[state=active]:translate-y-0",
      className
    )}
    // Radix scrolls the active trigger into view on focus, which yanks the page
    // on a horizontal rail. Same suppression the underline variant uses.
    onFocus={(e) => { e.currentTarget.scrollIntoView = () => {}; }}
    {...props}
  />
))
TabsPillTrigger.displayName = "TabsPillTrigger"

// ── TabsUnderline ─────────────────────────────────────────────────────────────
// Usage: wrap Tabs + TabsUnderlineList + TabsUnderlineTrigger + TabsContent

const TabsUnderlineList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "border-b border-border flex overflow-x-auto",
      className
    )}
    {...props}
  />
))
TabsUnderlineList.displayName = "TabsUnderlineList"

const TabsUnderlineTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "h-12 px-1 mr-6 text-sm font-medium whitespace-nowrap shrink-0 border-b-2 border-transparent -mb-px transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "text-muted-foreground hover:text-foreground",
      "data-[state=active]:text-foreground data-[state=active]:border-primary",
      className
    )}
            onFocus={(e) => { e.currentTarget.scrollIntoView = () => {}; }}
    {...props}
  />
))
TabsUnderlineTrigger.displayName = "TabsUnderlineTrigger"

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsPillList,
  TabsPillTrigger,
  TabsUnderlineList,
  TabsUnderlineTrigger,
}
